import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { ChevronDown, KeyRound, LogOut, UserCircle2 } from 'lucide-react'
import { Link } from 'react-router-dom'
import { useLogout, useMe } from '@/features/auth/hooks'
import { Button } from '@/components/ui/Button'
import { Skeleton } from '@/components/ui/Skeleton'
import { cn } from '@/lib/cn'

export function UserMenu() {
  const { data: me, isLoading } = useMe()
  const logout = useLogout()

  if (isLoading) return <Skeleton className="h-8 w-32" />
  if (!me) {
    return (
      <Button asChild variant="outline" size="sm">
        <Link to="/login">Sign in</Link>
      </Button>
    )
  }

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <Button variant="ghost" size="sm" className="gap-2">
          <UserCircle2 size={16} />
          <span className="max-w-[10rem] truncate">{me.display_name}</span>
          {me.branch_code ? (
            <span
              className="rounded bg-primary/10 px-1.5 py-0.5 font-mono text-[0.625rem] text-primary"
              title={me.branch_name ?? me.branch_code}
            >
              {me.branch_code}
            </span>
          ) : null}
          <ChevronDown size={14} />
        </Button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={4}
          className={cn(
            'z-50 min-w-[14rem] overflow-hidden rounded border border-border bg-popover p-1',
          )}
        >
          <div className="px-2 py-1.5 text-xs text-muted-fg">{me.email}</div>
          {me.branch_code ? (
            <div className="px-2 pb-1.5 text-[0.6875rem] text-muted-fg">
              <span className="font-mono">{me.branch_code}</span>
              {me.branch_name ? ` · ${me.branch_name}` : ''}
              {me.is_hq ? ' · global access' : ''}
            </div>
          ) : null}
          <DropdownMenu.Separator className="my-1 h-px bg-border" />
          <DropdownMenu.Item
            asChild
            className="cursor-pointer rounded px-2 py-1.5 text-sm outline-none data-[highlighted]:bg-muted"
          >
            <Link to="/access-requests/mine" className="flex items-center gap-2">
              <KeyRound size={14} />
              My access requests
            </Link>
          </DropdownMenu.Item>
          <DropdownMenu.Separator className="my-1 h-px bg-border" />
          <DropdownMenu.Item
            className="cursor-pointer rounded px-2 py-1.5 text-sm outline-none data-[highlighted]:bg-muted"
            onSelect={(e) => {
              // Prevent the menu from auto-closing before the mutation
              // settles — Radix calls onSelect synchronously and we
              // want to handle it ourselves once the mutation resolves.
              e.preventDefault()
              logout.mutate()
            }}
          >
            <span className="flex items-center gap-2">
              <LogOut size={14} />
              {logout.isPending ? 'Signing out…' : 'Sign out'}
            </span>
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  )
}
